import React, { useState, useEffect, useCallback } from 'react';
import {
    FaUndo,
    FaSpinner,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCheckCircle,
    FaHistory,
    FaTimes,
} from 'react-icons/fa';
import {
    getProfileRevertState,
    revertFullProfile,
    getOnboardingCrossDivisionReviewScope,
} from '../../services/onboardingService';
import CrossDivisionScopeModal from './CrossDivisionScopeModal';

const ProfileRevertControls = ({
    serverID,
    purgatoryID,
    enabled = true,
    onBusy,
    onReverted,
}) => {
    const [loading, setLoading] = useState(true);
    const [reverting, setReverting] = useState(false);
    const [stateData, setStateData] = useState(null);
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [crossDivisionScope, setCrossDivisionScope] = useState(null);
    const [checkingScope, setCheckingScope] = useState(false);

    const loadState = useCallback(async () => {
        if (!serverID || !purgatoryID) return;
        setLoading(true);
        setError(null);
        try {
            const res = await getProfileRevertState(serverID, purgatoryID);
            setStateData(res || {});
        } catch (err) {
            console.error('Failed to load profile revert state:', err);
            setError(err?.message || 'Unable to check whether this approval can be reverted.');
        } finally {
            setLoading(false);
        }
    }, [serverID, purgatoryID]);

    useEffect(() => {
        loadState();
    }, [loadState]);

    const handlePreRevert = async () => {
        if (!enabled || reverting || checkingScope || !stateData?.available || !stateData?.proposalID) return;
        setCheckingScope(true);
        setError(null);

        try {
            const scopeRes = await getOnboardingCrossDivisionReviewScope({
                serverID,
                purgatoryID,
                proposalID: stateData.proposalID,
                kind: 'profile',
                operation: 'revert',
            });

            if (scopeRes?.success === true && (scopeRes.affectedDivisions?.length > 1 || scopeRes.groupID)) {
                setCrossDivisionScope(scopeRes);
            } else {
                setShowConfirmModal(true);
            }
        } catch (scopeErr) {
            console.warn('Cross-division revert check fallback:', scopeErr);
            setShowConfirmModal(true);
        } finally {
            setCheckingScope(false);
        }
    };

    const handleRevertConfirm = async (crossDivisionConfirmation = '') => {
        if (reverting || !enabled || !stateData?.available || !stateData?.proposalID) return;
        setReverting(true);
        setError(null);
        if (onBusy) onBusy(true);

        try {
            const res = await revertFullProfile(
                serverID,
                purgatoryID,
                stateData.proposalID,
                typeof crossDivisionConfirmation === 'string' ? crossDivisionConfirmation : ''
            );
            if (res?.success !== true && res?.alreadyReverted !== true) {
                throw new Error('Revert was not confirmed by the server.');
            }
            setStateData({ available: false, reason: 'ALREADY_REVERTED' });
            setShowConfirmModal(false);
            setCrossDivisionScope(null);
            if (onReverted) onReverted();
        } catch (err) {
            console.error('Failed to revert full profile:', err);
            setError(err?.message || 'Failed to revert profile changes.');
        } finally {
            setReverting(false);
            if (onBusy) onBusy(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                fontSize: '13px',
                color: '#718096',
                fontStyle: 'italic',
            }}>
                <FaSpinner className="fa-spin" style={{ color: '#3182ce' }} />
                <span>Checking whether this approval can be reverted…</span>
            </div>
        );
    }

    const reason = stateData?.reason;

    // If candidate has never had an approved full profile, hide controls cleanly
    if (!error && reason === 'NO_APPROVED_FULL_PROFILE') {
        return null;
    }

    const isAvailable = stateData?.available === true;

    return (
        <div style={{
            backgroundColor: isAvailable ? '#fffaf0' : '#f7fafc',
            border: `1px solid ${isAvailable ? '#fbd38d' : '#e2e8f0'}`,
            borderRadius: '8px',
            padding: '14px 16px',
            margin: '12px 0',
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '8px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaHistory style={{ color: isAvailable ? '#c05621' : '#718096', fontSize: '14px' }} />
                    <span style={{
                        fontSize: '13px',
                        fontWeight: '700',
                        color: isAvailable ? '#7c2d12' : '#2d3748',
                    }}>
                        Test Approval Recovery (Profile Revert)
                    </span>
                </div>
                <button
                    type="button"
                    onClick={loadState}
                    disabled={!enabled || reverting}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#3182ce',
                        fontSize: '12px',
                        cursor: enabled && !reverting ? 'pointer' : 'not-allowed',
                        textDecoration: 'underline',
                        padding: 0,
                    }}
                >
                    Refresh recovery status
                </button>
            </div>

            {isAvailable ? (
                <div>
                    <p style={{
                        margin: '0 0 10px 0',
                        fontSize: '12.5px',
                        color: '#7c2d12',
                        lineHeight: '1.45',
                    }}>
                        An exact backup is available for this approval. Revert before loading another proposal if you want to undo this test approval and restore earlier values.
                    </p>
                    <button
                        type="button"
                        onClick={handlePreRevert}
                        disabled={!enabled || reverting || checkingScope}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#ffffff',
                            color: '#c05621',
                            border: '1px solid #dd6b20',
                            padding: '6px 14px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: enabled && !reverting && !checkingScope ? 'pointer' : 'not-allowed',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        }}
                    >
                        {reverting || checkingScope ? (
                            <>
                                <FaSpinner className="fa-spin" />
                                <span>{checkingScope ? 'Checking scope…' : 'Reverting profile…'}</span>
                            </>
                        ) : (
                            <>
                                <FaUndo />
                                <span>Revert Profile Changes</span>
                            </>
                        )}
                    </button>
                </div>
            ) : (
                <div style={{ fontSize: '12.5px', color: '#4a5568' }}>
                    {reason === 'EXACT_BACKUP_NOT_AVAILABLE' && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <FaInfoCircle style={{ color: '#718096', marginTop: '2px', flexShrink: 0 }} />
                            <span>This approval was made without an exact restore backup. Automatic revert is unavailable; missing fields cannot be safely distinguished from saved null values.</span>
                        </div>
                    )}
                    {reason === 'ALREADY_REVERTED' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2b6cb0' }}>
                            <FaCheckCircle />
                            <span>This approval has already been reverted. Earlier values restored.</span>
                        </div>
                    )}
                    {!reason && !error && (
                        <span>No restorable approval snapshot was found for this item.</span>
                    )}
                </div>
            )}

            {error && (
                <div style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#c53030',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '6px',
                }}>
                    <FaExclamationTriangle style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>{error}</span>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '520px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '14px',
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#742a2a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <FaUndo /> Revert this profile approval?
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowConfirmModal(false)}
                                disabled={reverting}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '16px',
                                    color: '#a0aec0',
                                    cursor: 'pointer',
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{
                            fontSize: '13px',
                            color: '#4a5568',
                            lineHeight: '1.5',
                            marginBottom: '18px',
                        }}>
                            <p style={{ margin: '0 0 10px 0' }}>
                                This restores the previous employee values and removes fields created by this approval. It also restores identity mappings and removes licence/insurance records created during this test.
                            </p>
                            <p style={{ margin: '0 0 10px 0', color: '#718096', fontSize: '12px' }}>
                                <strong>Safety notice:</strong> Audit evidence is retained. The operation stops if affected records have changed since approval.
                            </p>
                            <p style={{ margin: 0, color: '#a0aec0', fontSize: '11.5px' }}>
                                Does not undo external notifications, downloads, or actions made by another workflow.
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowConfirmModal(false)}
                                disabled={reverting}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: '1px solid #cbd5e0',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: reverting ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleRevertConfirm}
                                disabled={reverting}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#c53030',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: reverting ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {reverting ? (
                                    <>
                                        <FaSpinner className="fa-spin" />
                                        <span>Reverting…</span>
                                    </>
                                ) : (
                                    <span>Confirm Revert</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cross-Division Revert Intelligence Modal */}
            <CrossDivisionScopeModal
                isOpen={Boolean(crossDivisionScope)}
                scope={crossDivisionScope}
                kind="profile"
                operation="revert"
                onConfirm={(groupID) => handleRevertConfirm(groupID)}
                onCancel={() => setCrossDivisionScope(null)}
                busy={reverting}
            />
        </div>
    );
};

export default ProfileRevertControls;
